import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminAuthHeader } from "@/hooks/useAdminAuth";
import { extractApiError } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Soft-delete a contact from the CRM (sets `deleted_at` on the user doc).
 * The user is hidden from /admin/funnel-users but data is preserved in DB.
 */
export default function DeleteUserButton({ userId, userName, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: adminAuthHeader() });
      toast.success(`Contatto ${userName || ""} archiviato`);
      onDeleted?.(userId);
      setOpen(false);
    } catch (err) {
      const detail = extractApiError(err, "Errore sconosciuto");
      toast.error(`Impossibile archiviare: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          data-testid={`admin-delete-user-trigger-${userId}`}
          aria-label="Archivia contatto"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
          title="Archivia contatto"
        >
          <Trash2 size={15} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid={`admin-delete-dialog-${userId}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Archiviare questo contatto?</AlertDialogTitle>
          <AlertDialogDescription>
            <strong>{userName || userId}</strong> verrà nascosto dal CRM. I dati (eventi, note,
            pagamenti) <em>non vengono cancellati</em> dal database — il contatto può essere
            ripristinato manualmente in qualsiasi momento.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`admin-delete-cancel-${userId}`}>
            Annulla
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={confirmDelete}
            data-testid={`admin-delete-confirm-${userId}`}
            className="bg-red-600 hover:bg-red-700"
          >
            {busy ? "Archivio…" : "Archivia"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
